import React from 'react';
import { Box, Typography, Button, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, useTheme, Badge } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import GroupIcon from '@mui/icons-material/Group';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ExploreIcon from '@mui/icons-material/Explore';
import HomeIcon from '@mui/icons-material/Home'; // Example icons from data
import HealingIcon from '@mui/icons-material/Healing';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

// Mock data integration
import mockChats from '../data/mockChats.json';

// Utility function to map string names to actual MUI Icons
const IconMap = {
    Home: HomeIcon,
    Healing: HealingIcon,
    Work: WorkIcon,
    School: SchoolIcon,
    CalendarToday: CalendarTodayIcon
};

// Component for listing chat rooms (as requested, ChatRoomList is integrated here for layout simplicity)
const ChatRoomList = ({ title, rooms, onSelectRoom }) => {
    const theme = useTheme();
    
    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', px: 2, pt: 1, pb: 0.5 }}>
                {title}
            </Typography>
            <List disablePadding>
                {rooms.map((room) => {
                    const RoomIcon = IconMap[room.icon] || GroupIcon; // Default to GroupIcon

                    return (
                        <ListItem key={room.id} disablePadding>
                            <ListItemButton onClick={() => onSelectRoom(room.id)}>
                                <ListItemIcon>
                                    <RoomIcon sx={{ color: room.unread > 0 ? theme.palette.primary.main : theme.palette.text.secondary }} />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={room.name} 
                                    primaryTypographyProps={{ 
                                        fontWeight: room.unread > 0 ? 600 : 400 
                                    }}
                                />
                                {room.unread > 0 && (
                                    <Badge 
                                        badgeContent={room.unread} 
                                        color="secondary" 
                                        max={99} 
                                        sx={{ '& .MuiBadge-badge': { height: 20, minWidth: 20, borderRadius: '50%' } }}
                                    />
                                )}
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );
};


export const Sidebar = ({ onSelectRoom, onCreateChat }) => {
    const theme = useTheme();
    
    // Separate mock data into categories
    const myChats = mockChats.slice(0, 2);
    const publicChats = mockChats.slice(2);

    return (
        <Box 
            sx={{
                width: 300,
                flexShrink: 0,
                bgcolor: theme.palette.background.paper,
                borderRight: `1px solid ${theme.palette.divider}`,
                height: 'calc(100vh - 64px)', // Adjust height based on Header height (default 64px)
                overflowY: 'auto',
            }}
        >
            {/* Create Chat Button */}
            <Box sx={{ p: 2, pb: 1 }}>
                <Button 
                    variant="contained" 
                    color="secondary" 
                    fullWidth 
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={onCreateChat}
                    sx={{ py: 1.5 }}
                >
                    Create New Chat
                </Button>
            </Box>
            <Divider sx={{ my: 1 }} />

            {/* Main Navigation */}
            <List>
                <ListItemButton>
                    <ListItemIcon><FavoriteBorderIcon /></ListItemIcon>
                    <ListItemText primary="My Favorite Chats" />
                </ListItemButton>
                <ListItemButton>
                    <ListItemIcon><ExploreIcon /></ListItemIcon>
                    <ListItemText primary="Trending Topics" />
                </ListItemButton>
            </List>
            <Divider sx={{ my: 1 }} />
            
            {/* Chat Room Lists */}
            <ChatRoomList 
                title="My Chats" 
                rooms={myChats} 
                onSelectRoom={onSelectRoom} 
            />
            
            <ChatRoomList 
                title="Public Chats" 
                rooms={publicChats} 
                onSelectRoom={onSelectRoom} 
            />

        </Box>
    );
};